import _ from 'lodash';
import {safeMult, safeAdd, safeDivide, safeSubstract, safeAddReduce} from 'common/utils';
import {BudgetUtils} from 'app-ui-componentlibrary/dist/go/components/common/BudgetUtils';

const MANAGEMENT_COSTS = 'managementCosts';
// Map funding stream names from grant to the corresponding name in funding opportunity config
// if the names are not the same
const FUNDING_STREAM_NAME_MAPPING = {
  managementCosts: 'managementAdminCosts',
  indirectExpenses: 'indirectCosts',
};

const PROG_INCOME_FUNDING_STREAM_TYPES = ['construction', 'nonConstruction'];

export const moveManagementFundingStreams = (
  dataFS,
  sourceFSType,
  recipientOrgId,
  isRecipientPassSubOrg
) => {
  // add it if recipietn
  let data = _.cloneDeep(dataFS);
  const managementFS = {};
  _.forEach(data, (item) => {
    if (item.fundingStreamType === sourceFSType) {
      moveManagementFundingStreamsHelper(
        item.children,
        managementFS,
        {recipientOrgId},
        isRecipientPassSubOrg
      );
      item.children = _.omit(item.children, _.keys(managementFS));
    }
  });

  const managementItem = _.find(data, {fundingStreamType: MANAGEMENT_COSTS});
  if (managementItem) {
    const itemcode541 = _.find(managementItem.children, {itemCode: 541});
    if (itemcode541) {
      itemcode541.children = {...itemcode541.children, ...managementFS};
    } else {
      managementItem.children['itemCode-541'] = {itemCode: 541, children: managementFS};
    }
  } else {
    data.push({
      fundingStreamType: MANAGEMENT_COSTS,
      children: {
        'itemCode-541': {
          itemCode: 541,
          children: managementFS,
        },
      },
    });
  }

  return data;
};

const moveManagementFundingStreamsHelper = (
  children,
  managementFundingStreams,
  extraArgs,
  isRecipientPassSubOrg
) => {
  //
  _.forEach(children, (child, key) => {
    if (
      child.additionalInformation &&
      child.additionalInformation.solutionArea === 'MANAGEMENT_AND_ADMINISTRATION' &&
      (_.isEqual(extraArgs.recipientOrgId, extraArgs.orgIdPerformingWork) ||
        !isRecipientPassSubOrg)
    ) {
      managementFundingStreams[key] = child;
      return;
    }

    if (child.children) {
      moveManagementFundingStreamsHelper(
        child.children,
        managementFundingStreams,
        {
          ...extraArgs,
          orgIdPerformingWork: _.get(child, 'additionalInformation.orgIdPerformingWork'),
        },
        isRecipientPassSubOrg
      );
      child.children = _.omit(child.children, _.keys(managementFundingStreams));
    }
  });
};

const getCosts = (fsItem) => {
  // Initialize an empty array to collect the costs
  let costs = [];

  // Recursive function to traverse the object
  function traverse(o) {
    // If the object has children, traverse them
    if (o.children) {
      _.forEach(o.children, (child) => traverse(child));
    }

    // If the object has a cost property, collect the cost arrays
    if (o.cost) {
      costs.push(o.cost);
    }
  }

  traverse(fsItem);
  return costs;
};

// Sum actual totals by year
const sumTotalsByYear = (costsArray, itemFields) => {
  // Extract the nested arrays by their index position
  const indexedArrays = _.zip(...costsArray);

  // For each index, sum the actualTotal values using safeAdd
  return indexedArrays.map((arraysAtIndex) =>
    _.zipWith(
      ...arraysAtIndex.map((innerArray) =>
        innerArray.map((item) =>
          safeAdd(..._.map(itemFields, (itemField) => _.get(item, itemField)))
        )
      ),
      safeAdd
    )
  );
};

// Multiply with Appshare rates and round to 2 decimals for project totals
const zipProductByProjectTotals = (totals, rates) => {
  return _.map(totals, (total, index) => _.round(safeMult(total, rates[index]), 2));
};

// Multiply with Appshare rates and round to 2 decimals for federal
const zipProductByFederal = (totals, rates) => {
  return _.map(totals, (total, index) =>
    _.round(safeMult(total, safeDivide(rates[index], safeAdd(1, rates[index]))), 2)
  );
};

// Subtract appshare from total by year
const zipSubtract = (totals, nonFedShares) => {
  return _.map(totals, (total, index) =>
    _.round(safeSubstract(total, _.toNumber(nonFedShares[index])), 2)
  );
};

const zipAdd = (arr1, arr2) => {
  return _.map(arr1, (total, index) =>
    _.round(safeAdd(total, _.toNumber(arr2[index])), 2)
  );
};

// Calculate total fedShare and AppShare
export const calculateTotalAppAndFedShareByFS = ({
  totals,
  rates,
  applicantCostShareBasedOn,
}) => {
  let appShares = [];
  if (applicantCostShareBasedOn === 'federal') {
    appShares = zipProductByFederal(totals, rates);
  } else {
    appShares = zipProductByProjectTotals(totals, rates);
  }
  const fedShares = zipSubtract(totals, appShares);
  return {
    totals,
    appShares,
    fedShares,
  };
};

const isCostShareApproved = (costShareInformation, waiverRequested) => {
  if (costShareInformation) {
    const {waiverDetermination} = costShareInformation;
    return (
      (waiverRequested === true || waiverRequested === 'Yes') &&
      waiverDetermination === 'Approved'
    );
  }
  return false;
};

const updateBudgetBasedOnWaiver = (actualTotalFedShare, totals, previousGrantBudget) => {
  const actualTotal = safeAddReduce(totals);
  const previousTotal = safeAddReduce(previousGrantBudget.budgetSummary.totalCharges);

  if (actualTotal >= previousTotal) {
    actualTotalFedShare = previousGrantBudget.share.fedShare;
  } else {
    actualTotalFedShare = zipSubtract(totals, previousGrantBudget.share.appShare);
  }
  return actualTotalFedShare;
};

const getRatesFromDBConfig = (costShareConfig, fundingStreamType) => {
  const fsConfig = _.merge(
    _.get(costShareConfig, 'default', {}),
    _.get(costShareConfig, fundingStreamType)
  );
  if (!fsConfig.applicantCostShareRate) {
    return undefined;
  }
  return fsConfig;
};

export const calcTotalsByExcludingManagementCosts = ({
  fundingStreams,
  skipFS,
  maxAllowablePercentage,
  costShareConfig,
}) => {
  let actualTotalNonFedShareByYear = [];
  let actualTotalFedShareByYear = [];
  let mcLimit = 0;
  _.forEach(fundingStreams, (fsItem) => {
    const fundingStreamType = fsItem.fundingStreamType;
    const mappedFundingStreamType = _.get(
      FUNDING_STREAM_NAME_MAPPING,
      fundingStreamType,
      fundingStreamType
    );
    const fundingStreamRates = getRatesFromDBConfig(
      costShareConfig,
      mappedFundingStreamType
    );
    const costs = getCosts(fsItem);

    if (fundingStreamRates && fundingStreamType !== skipFS) {
      let totals = sumTotalsByYear(costs, ['total']);
      const {appShares, fedShares} = calculateTotalAppAndFedShareByFS({
        totals,
        rates: fundingStreamRates.applicantCostShareRate,
        applicantCostShareBasedOn: fundingStreamRates.applicantCostShareBasedOn,
      });
      actualTotalNonFedShareByYear = _.zipWith(
        actualTotalNonFedShareByYear,
        appShares,
        (accu, appShare) => safeAdd(accu || 0, appShare)
      );
      actualTotalFedShareByYear = _.zipWith(
        actualTotalFedShareByYear,
        fedShares,
        (accu, fedShare) => safeAdd(accu || 0, fedShare)
      );
    }
  });
  mcLimit = _.round(
    safeMult(safeAdd(actualTotalFedShareByYear), maxAllowablePercentage * 0.01),
    2
  );

  return {
    actualAppShare: actualTotalNonFedShareByYear,
    actualFedShare: actualTotalFedShareByYear,
    mcLimit: mcLimit,
  };
};

export const calcCloseoutReportBudgetShare = ({
  grantBudget,
  foConfig,
  fundingStreams,
  reductions,
  nonFederal,
  calculateFields = ['actualTotal'],
  waiverRequested,
  costShareInformation,
}) => {
  let actualTotalNonFedShare = [];
  let actualTotalFedShare = [];

  let totals = 0;

  const costShareConfig = foConfig.costShare;
  const foHandleProgIncome = _.get(foConfig, 'budget.foHandleProgIncome');
  const programIncome = _.get(grantBudget, 'nonFederal.programIncome');

  let programApplied = false;
  _.forEach(fundingStreams, (fsItem) => {
    const fundingStreamType = fsItem.fundingStreamType;
    const mappedFundingStreamType = _.get(
      FUNDING_STREAM_NAME_MAPPING,
      fundingStreamType,
      fundingStreamType
    );

    // Orginal code
    let fundingStreamRates = getRatesFromDBConfig(
      _.get(costShareConfig, 'fundingStreams'),
      mappedFundingStreamType
    );

    const costs = getCosts(fsItem);

    if (!fundingStreamRates) {
      fundingStreamRates = _.filter(
        foConfig.fundingStream,
        (item) => item.fundingStreamType === mappedFundingStreamType
      )[0];
    }

    if (fundingStreamRates) {
      totals = sumTotalsByYear(costs, calculateFields);

      if (
        PROG_INCOME_FUNDING_STREAM_TYPES.includes(fundingStreamType) &&
        !programApplied &&
        foHandleProgIncome == 'DeductionFromTotalCosts'
      ) {
        totals = BudgetUtils.getUpdatedTotalsByPeriod(
          totals,
          foHandleProgIncome,
          programIncome
        );
        programApplied = true;
      }

      if (reductions || nonFederal) {
        let programIncome = _.toNumber(_.get(nonFederal, 'programIncome', 0));
        let receivedInsurance = _.toNumber(_.get(reductions, 'receivedInsurance', 0));
        let otherFundingSources = _.toNumber(_.get(reductions, 'otherFundingSources', 0));
        totals = [
          _.round(
            safeSubstract(
              totals[0],
              safeAdd(programIncome, receivedInsurance, otherFundingSources)
            ),
            2
          ),
        ];
      }

      let {appShares, fedShares} = calculateTotalAppAndFedShareByFS({
        totals,
        rates: fundingStreamRates.applicantCostShareRate,
        applicantCostShareBasedOn: fundingStreamRates.applicantCostShareBasedOn,
      });

      //NonFedShare applications makes only appShare go down (in data, fedShare + appShare + programIncome = totalCosts).
      if (
        PROG_INCOME_FUNDING_STREAM_TYPES.includes(fundingStreamType) &&
        !programApplied &&
        foHandleProgIncome == 'ApplicationToNonFedShare'
      ) {
        appShares = BudgetUtils.getUpdatedTotalsByPeriod(
          appShares,
          foHandleProgIncome,
          programIncome
        );
        programApplied = true;
      }

      actualTotalNonFedShare = _.zipWith(
        actualTotalNonFedShare,
        appShares,
        (accu, appShare) => safeAdd(accu || 0, appShare)
      );
      actualTotalFedShare = _.zipWith(actualTotalFedShare, fedShares, (accu, fedShare) =>
        safeAdd(accu || 0, fedShare)
      );
    }
  });

  // Checks to make sure fedShare is not over the cap
  let fsTotals = zipAdd(actualTotalFedShare, actualTotalNonFedShare);

  if (
    _.sumBy(grantBudget.share.fedShare, (n) => {
      return _.toNumber(n);
    }) <
    _.sumBy(actualTotalFedShare, (n) => {
      return _.toNumber(n);
    })
  ) {
    actualTotalFedShare = _.isArray(grantBudget.share.fedShare)
      ? grantBudget.share.fedShare
      : [grantBudget.share.fedShare];
    actualTotalNonFedShare = zipSubtract(fsTotals, actualTotalFedShare);
  }

  //update actualFedShare and actualAppShare when cost share waiver exist
  const costShareApproved = isCostShareApproved(costShareInformation, waiverRequested);
  if (costShareApproved) {
    actualTotalFedShare = updateBudgetBasedOnWaiver(
      actualTotalFedShare,
      fsTotals,
      grantBudget
    );
    actualTotalNonFedShare = zipSubtract(fsTotals, actualTotalFedShare);
  }

  const share = grantBudget.share;
  return {
    ...share,
    actualAppRate: grantBudget.share.appRate,
    actualFedRate: grantBudget.share.fedRate,
    actualAppShare: actualTotalNonFedShare,
    actualFedShare: actualTotalFedShare,
  };
};

export default {
  calcCloseoutReportBudgetShare,
};
